import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { AddressComplementPart } from '@klareospace/pharos-lib-front-utils';

import { formatAddressComplementPart } from '../utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface AddressComplementPartsProps {
  readonly parts: AddressComplementPart[];
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Renders address complement parts (digicode, elevator, floor, complement, additional info) side
 * by side, each subsequent chip prefixed with "• ". Returns null for an empty list. Shared by the
 * order detail drawer and the task detail popin so the markup and separator logic live in one place.
 *
 * `additionalInfo` is the exception: `extractAddressComplementParts` always emits it last, and it
 * renders label-less as a full-width italic line (no bullet), matching the Lovable UI reference.
 */
export function AddressComplementParts({ parts }: AddressComplementPartsProps): React.JSX.Element | null {
  const { t } = useTranslation('orders');

  if (parts.length === 0) return null;

  return (
    <div className="mt-1 flex flex-wrap gap-x-2 gap-y-0.5 text-xs text-foreground">
      {parts.map((part, index) => {
        const value = formatAddressComplementPart(part, t);
        if (part.type === 'additionalInfo') {
          return (
            <span className="basis-full italic" key={part.type}>
              {value}
            </span>
          );
        }
        return <span key={part.type}>{index > 0 ? `• ${value}` : value}</span>;
      })}
    </div>
  );
}
